/**
 * Responsibility: call something when the user has nearly scrolled to the end.
 *
 * **It finds the scrolling element rather than being told which one it is.**
 * The results table sets its own height on desktop and scrolls internally,
 * while on a phone the page scrolls instead - so the container differs by
 * breakpoint, and it belongs to the kit's table rather than to this app. An
 * IntersectionObserver on a sentinel would not help: a sentinel placed after
 * a table that scrolls inside itself is never scrolled towards.
 *
 * So the ref is a handle on *where* in the tree to start looking, and the
 * hook walks up to whatever actually overflows.
 */
import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * How close to the end counts as near, in pixels.
 *
 * Roughly a screenful, so the next page is being fetched while there is
 * still something to read. Triggering at the very bottom means every page
 * ends in a wait.
 */
const NEAR_PX = 600;

function scrollParent(from: HTMLElement | null): HTMLElement | Window | null {
  let node = from?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    const scrolls = overflowY === 'auto' || overflowY === 'scroll';
    if (scrolls && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  // Nothing in the tree scrolls, so the document does.
  return from ? window : null;
}

export function useNearBottom(
  anchor: RefObject<HTMLElement | null>,
  onNear: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const container = scrollParent(anchor.current);
    if (!container) return undefined;

    const check = () => {
      const [scrollTop, viewport, total] =
        container instanceof Window
          ? [window.scrollY, window.innerHeight, document.documentElement.scrollHeight]
          : [container.scrollTop, container.clientHeight, container.scrollHeight];
      if (total - (scrollTop + viewport) < NEAR_PX) onNear();
    };

    // Once on arm, because a short first page may already end within a
    // screenful and would otherwise wait for a scroll that never comes.
    check();
    container.addEventListener('scroll', check, { passive: true });
    return () => container.removeEventListener('scroll', check);
  }, [anchor, onNear, enabled]);
}
